import { AlertTriangle, Home, MessageCircle, RefreshCw, WifiOff } from "lucide-react";
import { useRouter } from "@tanstack/react-router";
import { HTTPError } from "ky";
import { Button, RouteLink, StatusPage } from "../../components/ui";
import { tokenStorage } from "../../utils/tokenStorage";

type TErrorProps = {
  error?: Error;
  reset?: () => void;
};

export const Error = ({ error, reset }: TErrorProps) => {
  const router = useRouter();
  const isFullNetworkError = error instanceof TypeError;
  const isServiceUnavailable =
    error instanceof HTTPError && [502, 503, 504].includes(error.response.status);
  const isNetworkError = isFullNetworkError || isServiceUnavailable;
  const homeTo = tokenStorage.getAccessToken() ? "/app" : "/";

  const handleRetry = () => {
    if (reset) {
      reset();
    } else {
      void router.invalidate();
    }
  };

  return (
    <StatusPage
      gradientAccent="via-destructive/5"
      icon={
        isNetworkError ? (
          <WifiOff className="h-16 w-16 text-destructive" />
        ) : (
          <AlertTriangle className="h-16 w-16 text-destructive" />
        )
      }
      title={isNetworkError ? "Server Unavailable" : "Something Went Wrong"}
      description={
        isNetworkError
          ? "We couldn't reach the server. Please check your connection and try again."
          : "An unexpected error occurred. Please try again. If the issue persists, contact our support team."
      }
      actions={
        isNetworkError ? (
          <>
            <Button size="lg" variant="destructive" className="w-full" onClick={handleRetry}>
              <RefreshCw className="mr-2 h-5 w-5" />
              Try Again
            </Button>

            {isFullNetworkError ? (
              <RouteLink to="/contact" className="block w-full">
                <Button size="lg" variant="outline" className="w-full">
                  <MessageCircle className="mr-2 h-5 w-5" />
                  Contact Support
                </Button>
              </RouteLink>
            ) : (
              <RouteLink to={homeTo} className="block w-full">
                <Button size="lg" variant="outline" className="w-full">
                  <Home className="mr-2 h-5 w-5" />
                  Go Home
                </Button>
              </RouteLink>
            )}
          </>
        ) : (
          <>
            <Button size="lg" variant="destructive" className="w-full" onClick={handleRetry}>
              <RefreshCw className="mr-2 h-5 w-5" />
              Try Again
            </Button>

            <RouteLink to={homeTo} className="block w-full">
              <Button size="lg" variant="outline" className="w-full">
                <Home className="mr-2 h-5 w-5" />
                Go Home
              </Button>
            </RouteLink>
          </>
        )
      }
    />
  );
};
